import React, { useState } from 'react';
import { DocumentTextIcon, ClipboardDocumentIcon, ArrowDownTrayIcon, CheckIcon } from '@heroicons/react/24/outline';

// Chat Summary Panel - generates AI summary of chat messages
export default function ChatSummaryPanel({ messages, onClose }) {
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const generateSummary = async () => {
    setIsLoading(true);
    try {
      // Extract chat messages
      const chatMessages = messages || [];

      if (chatMessages.length === 0) {
        setSummary('No messages to summarize yet. Start chatting to generate a summary!');
        setIsLoading(false);
        return;
      }

      // Generate local summary based on message analysis
      const summaryResult = generateLocalSummary(chatMessages);
      setSummary(summaryResult);
    } catch (error) {
      console.error('Summary generation failed:', error);
      setSummary('Failed to generate summary. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const generateLocalSummary = (messages) => {
    // Analyze messages locally
    const totalMessages = messages.length;
    const uniqueUsers = new Set(messages.map(m => m.user?.name || m.user?.id || 'Unknown')).size;

    // Find questions (messages ending with ?)
    const questions = messages.filter(m => m.text?.trim().endsWith('?'));

    // Find messages with reactions
    const popularMessages = messages.filter(m => {
      const reactionCount = Object.values(m.reaction_counts || {}).reduce((a, b) => a + b, 0);
      return reactionCount > 0;
    });

    // Get pinned messages
    const pinnedMessages = messages.filter(m => m.pinned);

    // Build summary
    let summaryParts = [];

    summaryParts.push('CHAT SUMMARY');
    summaryParts.push('============\n');

    summaryParts.push('Overview:');
    summaryParts.push(`- Total messages: ${totalMessages}`);
    summaryParts.push(`- Participants: ${uniqueUsers}`);
    summaryParts.push(`- Questions asked: ${questions.length}`);
    summaryParts.push('');

    if (pinnedMessages.length > 0) {
      summaryParts.push('Pinned Messages:');
      pinnedMessages.slice(0, 5).forEach((m, i) => {
        const author = m.user?.name || 'Unknown';
        const text = m.text?.substring(0, 100) || '[attachment]';
        summaryParts.push(`${i + 1}. "${text}" - ${author}`);
      });
      summaryParts.push('');
    }

    if (questions.length > 0) {
      summaryParts.push('Questions Asked:');
      questions.slice(0, 5).forEach((q, i) => {
        const author = q.user?.name || 'Unknown';
        const text = q.text?.substring(0, 100) || '';
        summaryParts.push(`${i + 1}. "${text}" - ${author}`);
      });
      summaryParts.push('');
    }

    if (popularMessages.length > 0) {
      summaryParts.push('Popular Messages (with reactions):');
      popularMessages.slice(0, 5).forEach((m, i) => {
        const author = m.user?.name || 'Unknown';
        const text = m.text?.substring(0, 80) || '[attachment]';
        const reactions = Object.entries(m.reaction_counts || {})
          .map(([type, count]) => `${type}: ${count}`)
          .join(', ');
        summaryParts.push(`${i + 1}. "${text}" (${reactions}) - ${author}`);
      });
      summaryParts.push('');
    }

    // Recent activity
    const recentMessages = messages.slice(-5);
    if (recentMessages.length > 0) {
      summaryParts.push('Recent Messages:');
      recentMessages.forEach((m, i) => {
        const author = m.user?.name || 'Unknown';
        const text = m.text?.substring(0, 80) || '[attachment]';
        const time = m.created_at ? new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
        summaryParts.push(`- [${time}] ${author}: ${text}`);
      });
    }

    return summaryParts.join('\n');
  };

  const handleCopy = () => {
    if (summary) {
      navigator.clipboard.writeText(summary);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = () => {
    if (summary) {
      const blob = new Blob([summary], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `chat-summary-${new Date().toISOString().split('T')[0]}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  return (
    <div className="p-2 bg-[#1a1f35] rounded-lg border border-white/10">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <DocumentTextIcon className="w-3.5 h-3.5 text-purple-400" />
          <h3 className="text-white font-medium text-[11px]">Chat Summary</h3>
        </div>
        {!summary && !isLoading && (
          <button
            onClick={generateSummary}
            className="px-2 py-1 text-[10px] bg-[#6a1fbf] hover:bg-[#7a2fcf] text-white rounded-md transition-colors"
          >
            Generate
          </button>
        )}
      </div>

      {isLoading && (
        <div className="text-center py-3">
          <svg className="w-5 h-5 text-purple-400 animate-spin mx-auto mb-1" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-400 text-[10px]">Analyzing...</p>
        </div>
      )}

      {summary && !isLoading && (
        <>
          <div className="max-h-48 overflow-y-auto mb-2 scrollbar-thin scrollbar-thumb-white/10">
            <pre className="text-[10px] text-gray-300 whitespace-pre-wrap leading-relaxed font-sans pr-1">
              {summary}
            </pre>
          </div>
          <div className="flex gap-1.5">
            <button
              onClick={handleCopy}
              className="flex-1 flex items-center justify-center gap-1 px-2 py-1 border border-white/10 hover:bg-white/5 rounded-md transition-colors text-[10px] text-gray-300"
            >
              {copied ? (
                <>
                  <CheckIcon className="w-3 h-3 text-green-400" />
                  <span className="text-green-400">Copied!</span>
                </>
              ) : (
                <>
                  <ClipboardDocumentIcon className="w-3 h-3" />
                  <span>Copy</span>
                </>
              )}
            </button>
            <button
              onClick={handleDownload}
              className="flex-1 flex items-center justify-center gap-1 px-2 py-1 border border-white/10 hover:bg-white/5 rounded-md transition-colors text-[10px] text-gray-300"
            >
              <ArrowDownTrayIcon className="w-3 h-3" />
              <span>Download</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
